import { isEqual } from "lodash";
import { includeFields } from "../constants";
import SnackbarUtils from "../components/NotistackPlus";

interface singleIdentifiersMeta {
  module: string;
  section: string;
  menu: string;
}

export function logoutFunction() {
  localStorage.clear();
}

export const hasMenuAuth = (
  authList: API.AuthItem[] | undefined,
  code: string,
) => (authList ?? []).some((item) => item.name === code);

export const getCurrentModuleEditAllowed = (
  authList: API.AuthItem[] | undefined,
  pathname: string,
): boolean => {
  const routeRoot = pathname.split("/").filter(Boolean)[0];
  switch (routeRoot) {
    case "obligations":
      return hasMenuAuth(authList, "Edit_LCM_OBG");
    case "commitments":
      return hasMenuAuth(authList, "Edit_LCM_CMT");
    case "local-updates":
      return hasMenuAuth(authList, "Edit_LCM_LU");
    default:
      return false;
  }
};

export function getURLDomain() {
  const domain = process.env.REACT_APP_STG_DOMAIN || "";
  const splitByDomain = window.location.hostname.split(domain);
  const splitByDot = splitByDomain[0]?.split(".");
  const domainName = splitByDot[1] ? `${splitByDot[1]}.${domain}` : `${domain}`;
  return domainName;
}

export const toArray = (value: any) => {
  return Array.isArray(value) ? value : [value];
}

const changeEmptyArrToStr = (arr: any) => {
  return Array.isArray(arr) && arr.length === 0 ? "" : arr;
};

export const handleSelectedIdentifiersMeta = (
  originalMeta: Record<string, any>,
  originalIdentifierMap: Record<string, any>
) => {
  // const pickKeys = Object.keys(originalMeta);
  let resultObj: Record<string, singleIdentifiersMeta[]> = {};
  for (const key in originalIdentifierMap) {
    if (!Object.hasOwn(originalMeta, key)) {
      resultObj[`${key}::`] = [];
      continue;
    };
    const element = originalMeta[key];
    const field = element?.field ?? "";
    if (field && element.module && element.group && element.menu) {
      resultObj[`${key}::${field}`] = [
        {
          module: changeEmptyArrToStr(element.module),
          section: changeEmptyArrToStr(element.group),
          menu: changeEmptyArrToStr(element.menu),
        },
      ];
    } else {
      resultObj[`${key}::${field}`] = [];
    }
  }
  return resultObj;
};

export const sortFn = (a: any, b: any) => {
  return a.order - b.order;
};

export const replaceParams = (url: string, context: Record<string, any>) => {
  for (const key in context) {
    if (context.hasOwnProperty(key)) {
      const value = context[key];
      const pattern = "${" + key + "}";
      url = url.replace(pattern, value);
    }
  }
  return url;
};

export const replaceVariablesInUrl = (url: string) => {
  return url.replace(/(\w+)=\$\{\w+\}/g, (match, p1) => {
    return p1 + "=${" + p1 + "}";
  });
};

export const extractFirstPathSegment = (path: any) => {
  if (!path) return path;

  const secondSlashIndex = path.indexOf("/", 1);
  if (secondSlashIndex > 0) {
    return path.slice(0, secondSlashIndex);
  }
  return path;
};

/** Maps an LCM URL path to `sectionRulesModule` for lifecycle permission checks. */
export type LcmSectionRulesModule = "Obligations" | "Commitments" | "Local Update";

export function getSectionRulesModuleFromPathname(
  pathname: string,
): LcmSectionRulesModule | undefined {
  if (pathname.includes("/obligations")) {
    return "Obligations";
  }
  if (pathname.includes("/commitments")) {
    return "Commitments";
  }
  if (pathname.includes("/local-updates")) {
    return "Local Update";
  }
  return undefined;
}
/**
 * Data id for routes / deep links: splits `recordID` on `-` and returns the last
 * segment (supports variable prefix depth; id must not contain `-`).
 */
export function getRecordIdDataSegment(recordID: string): string {
  if (recordID == null || typeof recordID !== "string") {
    return "";
  }
  const trimmed = recordID.trim();
  const parts = trimmed.split("-").filter(Boolean);
  if (parts.length === 0) {
    return trimmed;
  }
  return parts[parts.length - 1] ?? trimmed;
}

export const formatPathToTitle = (path: string) => {
  if (!path) return "";
  if (path.includes("-")) {
    const words = path.replace(/^\//, "").replace(/-/g, " ").split(" ");
    const titledWords = words.map(
      (word) => word.charAt(0).toUpperCase() + word.slice(1)
    );
    return titledWords.join(" ");
  } else {
    return path.replace(/^\//, "").replace(/^\w/, (c) => c.toUpperCase());
  }
};

export function firstWordCase(parameter: string) {
  if (!parameter) return '';
  return parameter.slice(0, 1).toUpperCase() + parameter.slice(1).toLowerCase();
}

export function operatorName(name: any) {
  switch (name) {
    case "contains":
      return "Contains";
    case "equals":
      return "Equals";
    case "startsWith":
      return "Starts With";
    case "endsWith":
      return "Ends With";
    case "isEmpty":
      return "is Empty";
    case "isNotEmpty":
      return "is Not Empty";
    default:
      return "";
  }
}

export const canParseToJson = (str: any) => {
  if (typeof str == "string") {
    try {
      var obj = JSON.parse(str);
      if (typeof obj == "object" && obj) {
        return true;
      } else {
        return false;
      }
    } catch (e) {
      return false;
    }
  }
};

export const getAllIds = (tree: any, result: any) => {
  if (!Array.isArray(tree)) return [];
  for (let i = 0; i < tree.length; i++) {
    const node = tree[i];
    result.push(node.id);
    if (Array.isArray(node.children)) {
      getAllIds(node.children, result);
    }
  }
  return result;
};

export const handleSelectedIdentifiers = (
  filterList: any,
  identifierList = [] as any[],
  allSelected?: Record<string, boolean>
) => {
  let afterFilterList = {} as any;
  for (let key in filterList) {
    if (!identifierList.includes(key)) {
      if (Array.isArray(filterList[key])) {
        if (filterList[key].length > 0) {
          afterFilterList[key] = allSelected?.[key] ? ['all'] : filterList[key];
        }
      } else {
        afterFilterList[key] = allSelected?.[key] ? ['all'] : [filterList[key]];
      }
    }
  }
  return afterFilterList;
};

export const isLinkField = (
  sinlgeChangeField: Record<string, any>,
  settingsJson: Record<string, any>[]
) => {
  const curChangeTarget = sinlgeChangeField;
  const changeFieldSetting = settingsJson?.find(
    (i) => i.field === curChangeTarget.field
  );
  const curLinkFieldSetting = changeFieldSetting?.formRules?.linkField;

  return (
    Array.isArray(curLinkFieldSetting) &&
    curLinkFieldSetting?.length > 0 &&
    isEqual(changeFieldSetting?.linkFieldValue, curChangeTarget.value)
  );
};

export const navToLastLevel = (
  pathname: string,
  nav: (path: string) => void
) => {
  const regex = new RegExp(`(edit|view)-(obligation|commitment|local-update)`);
  const match = pathname.match(regex);
  if (match) {
    const moduleName = match[2];
    if (moduleName) {
      nav(`/${moduleName}s`);
    } else {
      nav("/lcm");
    }
  }
};

export const stripQueryString = (url: string) => {
  if (!url) return url;
  const questionMarkIndex = url.indexOf('?');
  if (questionMarkIndex === -1) {
    return url;
  }
  return url.substring(0, questionMarkIndex);
};

export const filterFormData = (
  formData?: Record<string, any>,
  extraFields?: string[],
): Record<string, any> => {
  if (!formData) return {};

  const excludeFields = new Set(
    (extraFields || []).map((field) => field.toLowerCase()),
  );

  return Object.keys(formData).reduce((filteredData, key) => {
    if (!excludeFields.has(key.toLowerCase())) {
      filteredData[key] = formData[key];
    }
    if (includeFields.includes(key.toLowerCase())) {
      filteredData[key] = formData[key];
    }
    return filteredData;
  }, {} as Record<string, any>);
};

export const handlePromiseError = (error: any) => {
  const curStatus = error?.response?.status;
  const curMsg = error?.response?.data?.message;
  if (curStatus === 401 || curMsg === "unauthorized") {
    return;
  }
  if (curStatus !== 400 && curStatus !== 500) {
    SnackbarUtils.error(curMsg);
  }
};

/** True when on add/edit/view (or metadata variants) under a side menu, not the first-level menu page. */
export function isMenuRecordSubPage(
  pathname: string,
  routeRecordId?: string | null,
): boolean {
  if (routeRecordId) {
    return true;
  }
  if (/\/add\/?$/.test(pathname)) {
    return true;
  }
  if (/\/addmetadata\//.test(pathname)) {
    return true;
  }
  if (/\/(edit|view)\/[^/]+/.test(pathname)) {
    return true;
  }
  if (/\/(edit|view)metadata\//.test(pathname)) {
    return true;
  }
  return false;
}

export function shouldShowMenuTitleTags(
  pathname: string,
  routeRecordId?: string | null,
): boolean {
  return !isMenuRecordSubPage(pathname, routeRecordId);
}